import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Pin, Reply, Smile } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { getChatMessages, sendMessage, reactToMessage, pinMessage, getEvent } from '../lib/api';
import { formatTimeAgo } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const REACTIONS = ['👍', '❤️', '😂', '😕'];

export default function ChatRoomPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await getChatMessages(eventId);
      setMessages(res.data);
    } catch (error) {
      // Silent fail for polling
    }
  }, [eventId]);

  const loadData = useCallback(async () => {
    try {
      const [eventRes, msgRes] = await Promise.all([
        getEvent(eventId),
        getChatMessages(eventId)
      ]);
      setEvent(eventRes.data);
      setMessages(msgRes.data);
    } catch (error) {
      toast.error('Failed to load chat');
    }
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    loadData();
    pollRef.current = setInterval(loadMessages, 3000);
    return () => clearInterval(pollRef.current);
  }, [eventId, loadData, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await sendMessage(eventId, newMessage.trim(), replyTo?.message_id);
      setNewMessage('');
      setReplyTo(null);
      await loadMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const handleReact = async (messageId, emoji) => {
    try {
      await reactToMessage(eventId, messageId, emoji);
      await loadMessages();
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const handlePin = async (messageId) => {
    try {
      await pinMessage(eventId, messageId);
      await loadMessages();
      toast.success('Message pinned');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pin');
    }
  };

  const isOrganizer = event?.organizer_id === user?.user_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent">
        <div className="animate-pulse text-muted-foreground">Loading chat...</div>
      </div>
    );
  }

  // Get pinned messages
  const pinnedMessages = messages.filter(m => m.is_pinned);

  return (
    <div className="h-screen bg-transparent">
      {/* Header */}
      <header className="glass border-b border-border/50 p-4 flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/chats')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold truncate">{event?.title}</h1>
          <p className="text-sm text-muted-foreground">{event?.chat_members} members</p>
        </div>
      </header>

      {/* Pinned Messages */}
      {pinnedMessages.length > 0 && (
        <div className="bg-primary/10 border-b border-primary/20 p-3">
          <div className="flex items-center gap-2 text-sm">
            <Pin className="w-4 h-4 text-primary" />
            <span className="font-medium text-primary">Pinned:</span>
            <span className="truncate">{pinnedMessages[pinnedMessages.length - 1].text}</span>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, i) => {
          const isOwn = msg.sender_id === user?.user_id;
          const isSystem = msg.is_system;
          const replyMsg = msg.reply_to ? messages.find(m => m.message_id === msg.reply_to) : null;

          if (isSystem) {
            return (
              <motion.div
                key={msg.message_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <span className="text-sm text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">
                  {msg.text}
                </span>
              </motion.div>
            );
          }

          return (
            <motion.div
              key={msg.message_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`message-${msg.message_id}`}
              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex gap-2 max-w-[80%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                {!isOwn && (
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {msg.sender_initials}
                    </AvatarFallback>
                  </Avatar>
                )}
                
                <div className={`space-y-1 ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                  {/* Reply Preview */}
                  {replyMsg && (
                    <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded border-l-2 border-primary">
                      <span className="font-medium">{replyMsg.sender_initials}:</span> {replyMsg.text.slice(0, 50)}...
                    </div>
                  )}
                  
                  {/* Message Bubble */}
                  <div className={`group relative rounded-2xl px-4 py-2 ${
                    isOwn 
                      ? 'bg-primary text-primary-foreground rounded-tr-sm' 
                      : 'bg-muted rounded-tl-sm'
                  }`}>
                    {msg.is_pinned && (
                      <Pin className="absolute -top-2 -right-2 w-4 h-4 text-primary" />
                    )}
                    <p className="break-words">{msg.text}</p>
                    <div className={`text-xs mt-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      {formatTimeAgo(msg.timestamp)}
                    </div>

                    {/* Actions */}
                    <div className={`absolute ${isOwn ? '-left-20' : '-right-20'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1`}>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Smile className="w-4 h-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2">
                          <div className="flex gap-1">
                            {REACTIONS.map(emoji => (
                              <button
                                key={emoji}
                                className="text-xl hover:scale-125 transition-transform p-1"
                                onClick={() => handleReact(msg.message_id, emoji)}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setReplyTo(msg)}>
                        <Reply className="w-4 h-4" />
                      </Button>
                      {isOrganizer && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePin(msg.message_id)}>
                          <Pin className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Reactions */}
                  {Object.entries(msg.reactions || {}).some(([_, users]) => users.length > 0) && (
                    <div className="flex gap-1 flex-wrap">
                      {Object.entries(msg.reactions).map(([emoji, users]) => 
                        users.length > 0 && (
                          <button
                            key={emoji}
                            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/50 text-sm hover:bg-muted transition-colors"
                            onClick={() => handleReact(msg.message_id, emoji)}
                          >
                            {emoji} <span className="text-xs">{users.length}</span>
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Preview */}
      {replyTo && (
        <div className="bg-muted/50 border-t border-border px-4 py-2 flex items-center gap-2">
          <Reply className="w-4 h-4 text-primary" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium">{replyTo.sender_initials}</span>
            <p className="text-sm text-muted-foreground truncate">{replyTo.text}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setReplyTo(null)}>Cancel</Button>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="border-t border-border p-4 flex gap-2">
        <Input
          data-testid="chat-input"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="flex-1 h-12"
          maxLength={500}
        />
        <Button 
          type="submit" 
          size="icon" 
          className="h-12 w-12"
          disabled={!newMessage.trim() || sending}
          data-testid="send-message-btn"
        >
          <Send className="w-5 h-5" />
        </Button>
      </form>
    </div>
  );
}
